import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { X, Plus, GripVertical, Trash2, ArrowLeft, Search, Target, Footprints, Activity, Dumbbell, Flame } from 'lucide-react';
import useTrainingPlanStore from '../../stores/trainingPlanStore';
import { useTheme } from '../../context/ThemeContext';
import { ExerciseLibrary } from '../../api/entities';
import ExerciseAccordion from './ExerciseAccordion';
import ConfirmDialog from './ConfirmDialog';
import DrawerBinderTabs from './DrawerBinderTabs';

export default function WorkoutEditDrawer() {
 const { themeClasses, isDarkMode } = useTheme();
 const editingWorkoutId = useTrainingPlanStore(state => state.ui.editingWorkoutId);
 const workout = useTrainingPlanStore(state => state.workouts[editingWorkoutId]);
 const drawerLeaf = useTrainingPlanStore(state => state.ui.drawerLeaf);
 const showWorkoutLeaf = useTrainingPlanStore(state => state.showWorkoutLeaf);
 const openExerciseSettings = useTrainingPlanStore(state => state.openExerciseSettings);
 
 const closeWorkoutDrawer = useTrainingPlanStore(state => state.closeWorkoutDrawer);
 const updateWorkout = useTrainingPlanStore(state => state.updateWorkout);
 const reorderExercises = useTrainingPlanStore(state => state.reorderExercises);
 const addExerciseToWorkout = useTrainingPlanStore(state => state.addExerciseToWorkout);
 const exercises = useTrainingPlanStore(state => state.exercises);
 const searchOpenForWorkoutId = useTrainingPlanStore(state => state.ui.searchOpenForWorkoutId);
 const openSearch = useTrainingPlanStore(state => state.openSearch);
 const closeSearch = useTrainingPlanStore(state => state.closeSearch);

 const language = localStorage.getItem('language') || 'english';
 const direction = language === 'hebrew' ? 'rtl' : 'ltr';

 const [localName, setLocalName] = useState('');
 const [activeDragId, setActiveDragId] = useState(null);

 // Search State
 const [isSearching, setIsSearching] = useState(false);
 const [libraryExercises, setLibraryExercises] = useState([]);
 const [searchTerm, setSearchTerm] = useState('');
 const [loadingLibrary, setLoadingLibrary] = useState(false);
 const [libraryError, setLibraryError] = useState(false);

 const [activeTab, setActiveTab] = useState('exercises'); // 'exercises' | 'settings'
 const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

 useEffect(() => {
 if (workout) {
 setLocalName(workout.day_name || '');
 }
 }, [workout?.day_name]);

 const loadLibrary = () => {
 setLoadingLibrary(true);
 setLibraryError(false);
 ExerciseLibrary.getAll()
 .then(data => setLibraryExercises(Array.isArray(data) ? data : []))
 .catch(err => {
 console.error(err);
 setLibraryError(true);
 })
 .finally(() => setLoadingLibrary(false));
 };

 useEffect(() => {
 if (!editingWorkoutId) {
 setIsSearching(false);
 return;
 }
 const shouldSearch = searchOpenForWorkoutId === editingWorkoutId;
 setIsSearching(shouldSearch);
 setActiveTab('exercises');
 if (!shouldSearch) return;
 if (libraryExercises.length > 0) return;
 loadLibrary();
 }, [editingWorkoutId, searchOpenForWorkoutId]);

 const handleNameBlur = () => {
 if (localName !== workout.day_name) {
 updateWorkout(editingWorkoutId, { day_name: localName });
 }
 };

 const getMuscleImagePath = (muscle) => {
 if (!muscle) return null;
 const lower = muscle.toLowerCase();
 let filename = '';
 
 // Detailed/Specific Muscles (Check these first so they don't get caught by generic terms)
 let folder = 'muscle-groups';
 if (lower.includes('front delt') || lower.includes('anterior delt') || lower.includes('כתף קדמית')) filename = 'shoulders';
 else if (lower.includes('side delt') || lower.includes('lateral delt') || lower.includes('כתף אמצעית')) filename = 'shoulders';
 else if (lower.includes('rear delt') || lower.includes('posterior delt') || lower.includes('כתף אחורית')) filename = 'shoulders';
 else if (lower.includes('upper chest') || lower.includes('חזה עליון')) filename = 'chest';
 else if (lower.includes('lower chest') || lower.includes('חזה תחתון')) filename = 'chest';
 else if (lower.includes('lower back') || lower.includes('גב תחתון')) filename = 'lower_back';
 else if (lower.includes('inner thigh') || lower.includes('adductor') || lower.includes('מקרבים') || lower.includes('ירך פנימית')) filename = 'inner_thighs';
 else if (lower.includes('mid') && lower.includes('back')) filename = 'midback';
 else if (lower.includes('neck') || lower.includes('צוואר')) filename = 'traps';
 else if (lower.includes('core') || lower.includes('ליבה')) filename = 'abs';
 else if (lower.includes('cardio') || lower.includes('אירובי') || lower.includes('לב ריאה')) { filename = 'cardio'; folder = 'workouts'; }
 else if (lower.includes('full body') || lower.includes('כללי') || lower.includes('גוף מלא')) filename = '';

 // Generic Muscles
 else if (lower.includes('ab') && !lower.includes('abductor')) filename = 'abs';
 else if (lower.includes('bicep') || lower.includes('יד קדמית')) filename = 'biceps';
 else if (lower.includes('calf') || lower.includes('calves') || lower.includes('תאומים')) filename = 'calves';
 else if (lower.includes('chest') || lower.includes('pectoral') || lower.includes('חזה')) filename = 'chest';
 else if (lower.includes('forearm') || lower.includes('אמות')) filename = 'forearms';
 else if (lower.includes('glute') || lower.includes('ישבן')) filename = 'glutes';
 else if (lower.includes('hamstring') || lower.includes('ירך אחורית') || lower.includes('המסטרינגס')) filename = 'hamstrings';
 else if (lower.includes('lat') || lower.includes('רחב גבי')) filename = 'lats';
 else if (lower.includes('oblique') || lower.includes('אלכסונים')) filename = 'obliques';
 else if (lower.includes('quad') || lower.includes('ארבע ראשי') || lower.includes('ירך קדמית')) filename = 'quads';
 else if (lower.includes('shoulder') || lower.includes('deltoid') || lower.includes('כתפיים')) filename = 'shoulders';
 else if (lower.includes('trap') || lower.includes('טרפזים')) filename = 'traps';
 else if (lower.includes('tricep') || lower.includes('יד אחורית')) filename = 'triceps';
 else if (lower.includes('back') || lower.includes('גב')) filename = 'midback';
 else if (lower.includes('בטן')) filename = 'abs';

 if (filename) {
 return `/assets/icons/fitness/${folder}/${isDarkMode ? 'dark' : 'light'}/${filename}.jpg`;
 }
 return null;
 };

 const handleDragStart = (event) => {
 setActiveDragId(event.active.id);
 };

 const handleDragEnd = (event) => {
 const { active, over } = event;
 setActiveDragId(null);
 if (!over) return;

 if (active.id !== over.id) {
 const oldIndex = workout.exerciseIds.indexOf(active.id);
 const newIndex = workout.exerciseIds.indexOf(over.id);
 reorderExercises(editingWorkoutId, oldIndex, newIndex);
 }
 };

 const handleOpenSearch = () => {
 setIsSearching(true);
 setSearchTerm('');
 openSearch(editingWorkoutId);
 if (libraryExercises.length === 0) {
 setLoadingLibrary(true);
 ExerciseLibrary.getAll()
 .then(data => setLibraryExercises(Array.isArray(data) ? data : []))
 .catch(err => console.error(err))
 .finally(() => setLoadingLibrary(false));
 }
 };

 const handleCloseSearch = () => {
 setIsSearching(false);
 setSearchTerm('');
 closeSearch();
 };

 const filteredLibrary = libraryExercises
 .filter(ex => {
 if (!searchTerm.trim()) return true;
 const name = (ex.exercise_name || '').toLowerCase();
 const nameHe = (ex.exercise_name_he || '').toLowerCase();
 const terms = searchTerm.toLowerCase().split(' ').filter(Boolean);
 
 // Allow match if all words are present anywhere in the string
 return terms.every(t => name.includes(t)) || terms.every(t => nameHe.includes(t));
 })
 .sort((a, b) => {
 if (!searchTerm.trim()) return 0;
 const term = searchTerm.toLowerCase().trim();
 const terms = term.split(' ').filter(Boolean);
 
 const aName = (a.exercise_name || '').toLowerCase();
 const aNameHe = (a.exercise_name_he || '').toLowerCase();
 const bName = (b.exercise_name || '').toLowerCase();
 const bNameHe = (b.exercise_name_he || '').toLowerCase();

 const getScore = (name, nameHe) => {
 // 1. Exact match
 if (name === term || nameHe === term) return 1; 
 
 // 2. Starts exactly with the full term
 if (name.startsWith(term) || nameHe.startsWith(term)) return 2; 
 
 // 3. Contains the full term as a discrete phrase/word sequence
 // (e.g., "Barbell Bench Press" matches "bench press")
 if (name.includes(` ${term} `) || name.startsWith(`${term} `) || name.endsWith(` ${term}`)) return 3;
 if (nameHe.includes(` ${term} `) || nameHe.startsWith(`${term} `) || nameHe.endsWith(` ${term}`)) return 3;
 
 // 4. Contains the exact substring without word boundaries
 if (name.includes(term) || nameHe.includes(term)) return 4;
 
 // 5. All search words exist as distinct whole words, just not in exact order
 const allWholeWords = terms.every(t => 
 name.match(new RegExp(`\\b${t}\\b`)) || nameHe.match(new RegExp(`\\b${t}\\b`))
 );
 if (allWholeWords) return 5;
 
 // 6. Scattered partial words matches
 return 6;
 };

 const scoreA = getScore(aName, aNameHe);
 const scoreB = getScore(bName, bNameHe);

 if (scoreA !== scoreB) return scoreA - scoreB;
 
 // Tie-breaker: Shorter name usually means the fundamental version of the exercise
 const lenA = (a.exercise_name || a.exercise_name_he || '').length;
 const lenB = (b.exercise_name || b.exercise_name_he || '').length;
 return lenA - lenB;
 });

 return (
 <>
 <AnimatePresence>
 {(editingWorkoutId && workout) && (
 <>
 {/* Backdrop */}
 <motion.div 
 initial={{ opacity: 0 }}
 animate={{ opacity: 1 }}
 exit={{ opacity: 0 }}
 transition={{ duration: 0.3 }}
 className="fixed inset-0 bg-ink/20 dark:bg-ink/40 z-40" 
 onClick={closeWorkoutDrawer}
 />
 
 {/* Drawer */}
 <motion.div 
 initial={{ x: language === 'hebrew' ? '-100%' : '100%' }}
 animate={{ x: 0 }}
 exit={{ x: language === 'hebrew' ? '-100%' : '100%' }}
 transition={{ type: "spring", damping: 25, stiffness: 200 }}
 className={`fixed inset-y-0 ${language === 'hebrew' ? 'left-0 border-r' : 'right-0 border-l'} w-full sm:w-[640px] md:w-[720px] bg-surface dark:bg-surface-dark text-ink dark:text-ink-dark z-50 flex flex-col border-line dark:border-line-dark overflow-visible`}
 dir={direction}
 >
 {drawerLeaf === 'workout' && workout.exerciseIds?.length > 0 && (
 <DrawerBinderTabs
 active="workout"
 exercises={workout.exerciseIds.map((id) => ({
 id,
 label: exercises[id]?.exercise_name || (language === 'hebrew' ? 'תרגיל' : 'Exercise'),
 }))}
 onWorkout={showWorkoutLeaf}
 onExercise={openExerciseSettings}
 rtl={language === 'hebrew'}
 />
 )}
 {/* Header */}
 <div className="px-6 pt-5 border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark flex flex-col gap-4">
 <div className="flex justify-between items-start gap-4">
 <div className="flex-1 min-w-0">
 {isSearching ? (
 <div className="flex items-center gap-2 mt-1 mb-1">
 <button 
 type="button"
 onClick={handleCloseSearch}
 className="p-2 min-h-11 min-w-11 inline-flex items-center justify-center hover:bg-canvas dark:hover:bg-canvas-dark rounded-md transition-colors duration-120 text-ink-muted outline-none focus-visible:ring-2 focus-visible:ring-forest"
 aria-label={language === 'hebrew' ? 'חזור' : 'Back'}
 title={language === 'hebrew' ? 'חזור' : 'Back'}
 >
 <ArrowLeft size={18} className={language === 'hebrew' ? 'rotate-180' : ''} />
 </button>
 <h2 className="text-lg font-semibold tracking-tight">{language === 'hebrew' ? 'הוסף תרגיל' : 'Add Exercise'}</h2>
 </div>
 ) : (
 <>
 <h2 className={`font-semibold text-2xl tracking-tight truncate ${themeClasses.textPrimary}`}>
 {workout.day_name || (language === 'hebrew' ? 'אימון חדש' : 'New Workout')}
 </h2>
 <div className="text-sm font-medium text-ink-muted mt-1">
 {workout.exerciseIds.length} {language === 'hebrew' ? 'תרגילים' : 'exercises'}
 </div>
 </>
 )}
 </div>
 <div className="flex items-center gap-2">
 <button 
 type="button" 
 aria-label={language === 'hebrew' ? 'מחק אימון' : 'Delete Workout'}
 onClick={() => setDeleteConfirmOpen(true)}
 className="p-2.5 min-h-11 min-w-11 flex items-center justify-center text-red-500 hover:text-red-600 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-full transition-colors shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-red-500"
 title={language === 'hebrew' ? 'מחק אימון' : 'Delete Workout'}
 >
 <Trash2 size={20} />
 </button>
 <button 
 type="button" 
 aria-label="Close"
 onClick={closeWorkoutDrawer} 
 className="p-2.5 min-h-11 min-w-11 flex items-center justify-center bg-surface dark:bg-surface-dark hover:bg-surface dark:hover:bg-surface-dark rounded-full transition-colors shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-forest"
 >
 <X size={20} />
 </button>
 </div>
 </div>

 {/* Tabs */}
 {!isSearching && (
 <div className="flex gap-6 mt-2">
 <button 
 onClick={() => setActiveTab('exercises')}
 className={`pb-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'exercises' ? 'border-forest text-forest dark:text-forest-ondark' : 'border-transparent text-ink-muted hover:text-ink dark:hover:text-ink-muted-dark'}`}
 >
 {language === 'hebrew' ? 'תרגילים' : 'Exercises'}
 </button>
 <button 
 onClick={() => setActiveTab('settings')}
 className={`pb-3 text-sm font-bold border-b-2 transition-colors ${activeTab === 'settings' ? 'border-forest text-forest dark:text-forest-ondark' : 'border-transparent text-ink-muted hover:text-ink dark:hover:text-ink-muted-dark'}`}
 >
 {language === 'hebrew' ? 'הגדרות' : 'Settings'}
 </button>
 </div>
 )}
 </div>

 {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar flex flex-col relative bg-surface dark:bg-surface-dark">
 
 {/* Settings Tab */}
 {activeTab === 'settings' && !isSearching && (
 <div className="p-6 space-y-6 animate-in fade-in duration-300">
 <div className="space-y-2">
 <label className="text-sm font-bold text-ink dark:text-ink-dark">
 {language === 'hebrew' ? 'שם האימון' : 'Workout Name'}
 </label>
 <input 
 type="text"
 value={localName}
 onChange={(e) => setLocalName(e.target.value)}
 onBlur={handleNameBlur}
 onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
 placeholder={language === 'hebrew' ? 'למשל: אימון A' : 'e.g. Workout A'}
 className="w-full px-4 py-3 bg-surface dark:bg-canvas-dark border border-line dark:border-line-dark rounded-md focus:border-forest outline-none transition-colors font-medium"
 />
 </div>
 <div className="space-y-2">
 <label className="text-sm font-bold text-ink dark:text-ink-dark">
 {language === 'hebrew' ? 'הערות לאימון (אופציונלי)' : 'Workout Notes (Optional)'}
 </label>
 <textarea 
 value={workout.notes || ''}
 onChange={(e) => updateWorkout(editingWorkoutId, { notes: e.target.value })}
 placeholder={language === 'hebrew' ? 'דגשים מיוחדים לאימון...' : 'Special instructions for this workout...'}
 className="w-full px-4 py-3 bg-surface dark:bg-canvas-dark border border-line dark:border-line-dark rounded-md focus:border-forest outline-none transition-colors font-medium resize-none"
 rows={4}
 />
 </div>
 </div>
 )}

 {/* Main Edit View (Exercises Tab) */}
 <div className={`p-6 space-y-4 transition-all duration-300 ${(isSearching || activeTab !== 'exercises') ? 'hidden' : 'block'}`}>
 <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
 <SortableContext items={workout.exerciseIds} strategy={verticalListSortingStrategy}>
 {workout.exerciseIds.map(exId => (
 <ExerciseAccordion key={exId} exerciseId={exId} workoutId={editingWorkoutId} />
 ))}
 </SortableContext>
 
 <DragOverlay>
 {activeDragId ? (
 <div className="p-4 bg-surface dark:bg-surface-dark border border-forest rounded-md opacity-95 scale-105 cursor-grabbing font-bold flex items-center gap-3">
 <GripVertical size={20} className="text-ink-muted" />
 {exercises[activeDragId]?.exercise_name || 'Exercise'}
 </div>
 ) : null}
 </DragOverlay>
 </DndContext>

              <button 
                onClick={handleOpenSearch}
                className="mt-4 w-full py-5 border-2 border-dashed border-line dark:border-line-dark rounded-md text-ink-muted dark:text-ink-muted-dark font-bold hover:bg-forest-faint dark:hover:bg-forest-faint-dark hover:text-forest dark:hover:text-forest-ondark hover:border-forest transition-all flex items-center justify-center gap-2 "
              >
 <Plus size={22} strokeWidth={2.5} /> {language === 'hebrew' ? 'הוסף תרגיל' : 'Add Exercise'}
 </button>
 </div>

 {/* Search View Inline */}
 {isSearching && (
 <div className="flex-1 flex flex-col bg-surface dark:bg-surface-dark">
 <div className="px-4 py-3 border-b border-line dark:border-line-dark bg-surface dark:bg-surface-dark">
 <div className="relative">
 <Search className={`absolute top-1/2 -translate-y-1/2 text-ink-muted pointer-events-none ${language === 'hebrew' ? 'right-3' : 'left-3'}`} size={16} />
 <input 
 type="text"
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 placeholder={language === 'hebrew' ? 'חיפוש תרגיל...' : 'Search exercises...'}
 autoFocus
 className={`w-full min-h-11 py-2 text-[13px] bg-canvas dark:bg-canvas-dark border border-line dark:border-line-dark rounded-md outline-none focus:border-forest focus:ring-2 focus:ring-forest placeholder:text-ink-muted ${language === 'hebrew' ? 'pr-10 pl-3' : 'pl-10 pr-3'}`}
 />
 </div>
 </div>

 <div className="flex-1 overflow-y-auto">
 {loadingLibrary ? (
 <div className="px-4 divide-y divide-line dark:divide-line-dark animate-pulse">
 {[1, 2, 3, 4].map((i) => (
 <div key={`lib-skel-${i}`} className="h-14 flex items-center gap-3">
 <div className="h-3 w-40 rounded bg-line dark:bg-line-dark" />
 <div className="ms-auto flex gap-2">
 <div className="w-7 h-7 rounded bg-line dark:bg-line-dark" />
 <div className="w-7 h-7 rounded bg-line dark:bg-line-dark" />
 </div>
 </div>
 ))}
 </div>
 ) : libraryError ? (
 <div className="flex flex-col items-center justify-center p-8 text-center gap-3">
 <p className="text-red-700 dark:text-red-300 text-[13px] font-medium">{language === 'hebrew' ? 'שגיאה בטעינת ספריית התרגילים.' : 'Couldn\'t load the exercise library.'}</p>
 <button type="button" onClick={loadLibrary} className="min-h-11 px-4 py-2 rounded-md border border-line dark:border-line-dark text-[13px] font-semibold text-ink dark:text-ink-dark hover:bg-canvas dark:hover:bg-canvas-dark transition-colors duration-120 outline-none focus-visible:ring-2 focus-visible:ring-forest">
 {language === 'hebrew' ? 'נסה שוב' : 'Retry'}
 </button>
 </div>
 ) : filteredLibrary.length === 0 ? (
 <div className="flex flex-col items-center justify-center p-8 text-center gap-2">
 <p className="text-ink-muted text-[13px]">{language === 'hebrew' ? `לא נמצאו תרגילים התואמים ל"${searchTerm}".` : `No exercises match "${searchTerm}".`}</p>
 <button type="button" onClick={() => setSearchTerm('')} className="min-h-11 px-3 text-forest dark:text-forest-ondark text-[13px] font-semibold hover:underline outline-none focus-visible:ring-2 focus-visible:ring-forest rounded-md">
 {language === 'hebrew' ? 'נקה חיפוש' : 'Clear search'}
 </button>
 </div>
 ) : (
 <div className="divide-y divide-line dark:divide-line-dark">
 {filteredLibrary.map(ex => {
 const exName = language === 'hebrew' ? (ex.exercise_name_he || ex.exercise_name) : (ex.exercise_name || ex.exercise_name_he);
 
 const rawPrimary = ex.primary_muscles || ex.muscle_groups_primary || [];
 const rawSecondary = ex.secondary_muscles || ex.muscle_groups_secondary || [];
 const combinedMuscles = Array.from(new Set([...rawPrimary, ...rawSecondary]));
 
 if (combinedMuscles.length === 0) {
 combinedMuscles.push(ex.category || (language === 'hebrew' ? 'כללי' : 'General'));
 }
 
 const displayMuscles = combinedMuscles.slice(0, 3);
 const rankStyles = [
 { border: 'border-amber-400 dark:border-amber-500', badge: 'bg-amber-500 text-white' },
 { border: 'border-line dark:border-line', badge: 'bg-moss text-white' },
 { border: 'border-orange-800 dark:border-orange-600', badge: 'bg-orange-800 text-white' },
 ];

 return (
 <div 
 key={ex.id}
 onClick={() => addExerciseToWorkout(editingWorkoutId, ex)}
 className="px-4 py-2.5 bg-surface dark:bg-surface-dark hover:bg-forest/10 dark:hover:bg-forest/20 cursor-pointer transition-colors duration-120 group flex justify-between items-center gap-3 min-h-[72px] outline-none focus-visible:ring-2 focus-visible:ring-forest"
 >
 <div className="flex-1 min-w-0">
 <h4 className="font-medium text-[13px] text-ink dark:text-ink-dark truncate">{exName}</h4>
 {ex.mechanic && (
 <span className="block text-[11px] text-ink-muted mt-0.5 truncate">
 {language === 'hebrew' ? (ex.mechanic === 'compound' ? 'מורכב' : 'מבודד') : (ex.mechanic === 'compound' ? 'Compound' : 'Isolation')}
 {ex.equipment && ` • ${ex.equipment}`}
 </span>
 )}
 </div>
 <div className="flex items-center gap-3 shrink-0">
 {displayMuscles.map((muscle, idx) => {
 const imagePath = getMuscleImagePath(muscle);
 const rank = rankStyles[idx] || rankStyles[2];
 return (
 <div key={idx} className="flex flex-col items-center gap-1 w-14">
 <div className={`relative w-12 h-12 rounded-md overflow-hidden border-2 ${rank.border} bg-canvas dark:bg-canvas-dark flex items-center justify-center`}>
 {imagePath ? (
 <img src={imagePath} alt={muscle} className="w-full h-full object-cover" />
 ) : (
 <Dumbbell size={20} className="text-ink-muted" />
 )}
 <span className={`absolute top-0 ${language === 'hebrew' ? 'left-0 rounded-br' : 'right-0 rounded-bl'} min-w-[16px] h-4 px-0.5 text-[10px] font-semibold leading-4 text-center ${rank.badge}`}>
 {idx + 1}
 </span>
 </div>
 <span className="text-[10px] font-medium text-ink-muted text-center leading-none capitalize truncate w-full">{muscle}</span>
 </div>
 );
 })}
 <span className="min-h-9 min-w-9 inline-flex items-center justify-center text-ink-muted group-hover:text-forest transition-colors duration-120">
 <Plus size={16} strokeWidth={2} />
 </span>
 </div>
 </div>
 );
 })}
 </div>
 )}
 </div>
 </div>
 )}
 </div>
 </motion.div>
 </>
 )}
 </AnimatePresence>

 <ConfirmDialog
 open={deleteConfirmOpen}
 title={language === 'hebrew' ? `מחק אימון?` : `Delete ${workout?.day_name}?`}
 body={language === 'hebrew' ? 'כל התרגילים ביום זה יימחקו.' : 'Exercises on this day will be removed.'}
 confirmLabel={language === 'hebrew' ? 'מחק' : 'Delete'}
 isDestructive={true}
 onConfirm={() => {
 setDeleteConfirmOpen(false);
 useTrainingPlanStore.getState().removeWorkout(editingWorkoutId);
 closeWorkoutDrawer();
 }}
 onCancel={() => setDeleteConfirmOpen(false)}
 language={language}
 />
 </>
 );
}
